import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/layout/SmartShell";
import SmartExportModal from "@/components/exports/SmartExportModal";
import api, { BACKEND_URL } from "@/lib/api";
import { Download, Trash2, Loader2, FilmIcon, ImageIcon, Share2 } from "lucide-react";

export default function RendersPage() {
  const { user, loading } = useAuth();
  const [renders, setRenders] = useState(null);
  const [error, setError] = useState("");
  const [exportTarget, setExportTarget] = useState(null);   // { render_id, kind, aspect }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/renders");
      setRenders(data.renders);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;

  const onDelete = async (id) => {
    if (!window.confirm("Delete this render? This cannot be undone.")) return;
    try {
      await api.delete(`/renders/${id}`);
      setRenders((r) => r.filter((x) => x.render_id !== id));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  return (
    <DashboardShell>
      <div className="mb-10">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">Library</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">My Renders</h1>
        <p className="text-[#A0A0A0] mt-4">All your generated images and videos in one place.</p>
      </div>

      {error && <div className="mb-6 px-4 py-3 border border-red-500/40 bg-red-500/10 text-red-300 text-sm">{error}</div>}

      {renders === null && (
        <div className="text-center py-20 text-[#A0A0A0]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      )}
      {renders && renders.length === 0 && (
        <div className="border border-[#2A2A2A] bg-[#0F0F0F] py-20 px-6 text-center" data-testid="empty-renders">
          <p className="text-[#A0A0A0] mb-6">No renders yet. Make your first one.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/generate/video" className="bg-[#D4AF37] text-[#080808] font-bold px-5 py-2.5 hover:bg-[#F0C84B] transition-colors">Generate Video</Link>
            <Link to="/generate/image" className="border border-[#2A2A2A] hover:border-[#D4AF37] px-5 py-2.5 transition-colors">Generate Image</Link>
          </div>
        </div>
      )}
      {renders && renders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="renders-grid">
          {renders.map((r) => (
            <div key={r.render_id} className="border border-[#2A2A2A] bg-[#0F0F0F]" data-testid={`render-${r.render_id}`}>
              <div className="aspect-square bg-black flex items-center justify-center overflow-hidden">
                {r.kind === "image" ? (
                  <img src={`${BACKEND_URL}${r.preview_url}`} alt={r.prompt} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <video src={`${BACKEND_URL}${r.preview_url}`} controls playsInline className="w-full h-full object-contain" />
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  {r.kind === "video" ? <FilmIcon className="w-4 h-4 text-[#D4AF37]" /> : <ImageIcon className="w-4 h-4 text-[#D4AF37]" />}
                  <span className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{r.kind} · {r.tier_at_render}{r.watermarked ? " · watermarked" : ""}</span>
                </div>
                <p className="text-xs text-[#F4F4F0] mb-3 line-clamp-2" title={r.prompt}>{r.prompt}</p>
                <div className="flex gap-2">
                  <a
                    href={`${BACKEND_URL}${r.download_url}`}
                    data-testid={`download-${r.render_id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0] py-2 text-xs transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                  <button
                    onClick={() => setExportTarget({
                      render_id: r.render_id,
                      kind: r.kind,
                      aspect: (r.params || {}).aspect || null,
                    })}
                    data-testid={`smart-export-${r.render_id}`}
                    className="border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] px-3 py-2 text-xs transition-colors"
                    aria-label="Smart Export"
                    title="Smart Export — re-format for Meta / TikTok / YouTube"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(r.render_id)}
                    data-testid={`delete-${r.render_id}`}
                    className="border border-[#2A2A2A] hover:border-red-500/60 text-[#A0A0A0] hover:text-red-400 px-3 py-2 transition-colors"
                    aria-label="Delete render"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <SmartExportModal
        open={!!exportTarget}
        onClose={() => setExportTarget(null)}
        renderId={exportTarget?.render_id}
        sourceAspect={exportTarget?.aspect}
        sourceKind={exportTarget?.kind}
      />
    </DashboardShell>
  );
}
